import React, { useState } from "react";
import { CircularProgress, Box } from "@mui/material";

/**
 * Componente para cargar una imagen con soporte para propiedades de estilo y manejo de carga.
 * @component
 * @param {object} props - Las propiedades que se pasan al componente.
 * @param {string} props.src - La URL de la imagen que se va a cargar.
 * @param {string} props.alt - Texto alternativo para la imagen (útil para accesibilidad).
 * @param {number} props.width - El ancho de la imagen (en píxeles o como valor CSS).
 * @param {number} props.height - La altura de la imagen (en píxeles o como valor CSS).
 * @param {string} [props.borderRadius] - El radio del borde de la imagen (opcional). Puede ser un valor CSS como "10px" o "50%".
 * @returns {JSX.Element} El componente `ImageLoader`, que renderiza una imagen con las propiedades pasadas.
 */
const ImageLoader = ({
  src,
  alt,
  width,
  height,
  borderRadius,
  objectPosition,
  style,
}) => {
  /**
   * Estado que controla si los datos están cargando.
   * @const
   * @type {[boolean, Function]} loading - El valor del estado (booleano) y la función `setLoading` para actualizarlo.
   * @description `loading` se usa para mostrar o esconder un indicador de carga mientras se están procesando datos.
   */
  const [loading, setLoading] = useState(true);
  /**
   * Estado que indica si ocurrió un error durante el proceso de carga.
   * @const
   * @type {[boolean, Function]} error - El valor del estado (booleano) y la función `setError` para actualizarlo.
   * @description `error` se utiliza para mostrar un mensaje o componente de error en la interfaz de usuario si ocurre un fallo en la carga de datos.
   */
  const [error, setError] = useState(false);
  /**
   * Función que se llama cuando la carga de datos es exitosa.
   * @function handleLoad
   * @description Esta función se invoca cuando la operación de carga ha terminado correctamente, estableciendo `loading` en `false` y `error` en `false`.
   */
  const handleLoad = () => {
    setLoading(false);
    setError(false);
  };
  /**
   * Función que se llama cuando ocurre un error en la carga de datos.
   * @function handleError
   * @description Esta función se invoca cuando hay un error durante la operación de carga, estableciendo `loading` en `false` y `error` en `true`.
   */
  const handleError = () => {
    setLoading(false);
    setError(true);
  };
  /**
   * Estilos de diseño utilizados en el componente.
   * @type {Object}
   */
  const design = {
    Aling: {
      position: "relative",
      width,
      height,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    Load: {
      display: loading ? "none" : "block", // Oculta la imagen mientras carga
    },
  };

  return (
    <Box sx={design.Aling}>
      {loading && !error && <CircularProgress />}
      {error && (
        <Box
          component="img"
          src="https://via.placeholder.com/350"
          alt="Imagen de respaldo"
          width={width}
          height={height}
        />
      )}
      {!error && (
        <Box
          component="img"
          src={src}
          alt={alt}
          width={width}
          height={height}
          onLoad={handleLoad}
          onError={handleError}
          sx={{
            ...design.Load,
            objectFit: "cover",
            objectPosition: objectPosition ? objectPosition : null,

            ...style,
          }}
          borderRadius={borderRadius}
        />
      )}
    </Box>
  );
};

export default ImageLoader;
